import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Select,
  Card,
  CardBody,
  Icon,
  Button,
  Flex,
  Badge,
  SimpleGrid,
  Spinner,
  Input,
  FormControl,
  FormLabel,
  Divider,
  Tooltip,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import {
  FileText,
  Buildings,
  DownloadSimple,
  CalendarBlank,
  MagnifyingGlass,
  Receipt,
  Money,
  Bank,
  FileArrowDown,
  FunnelSimple,
  X,
  CaretRight,
  ArrowLeft,
  Eye,
} from 'phosphor-react';
import { toast } from 'sonner';
import { useParams, useNavigate } from 'react-router-dom';
import SecaoClienteLayout from '../layout';
import { api } from '../../../services/api';
import { formatCNPJ } from '../../../utils/FormatFunctions';

interface ClienteData {
  id: number;
  guid: string;
  nome: string;
  email: string;
  empresas?: Array<{
    id: number;
    external_id: string;
    razao_social: string;
    nome_fantasia: string;
    cnpj: string | null;
  }>;
}

interface Guia {
  id: string;
  tipo: string;
  competencia: string;
  nome_arquivo: string;
}

interface GuiasResponse {
  data: {
    guias: Guia[];
    quantidade: number;
    regime: string;
    empresa_id: number;
    periodo_inicio: string;
    periodo_fim: string;
  };
  message: string;
}

interface DownloadResponse {
  url: string;
  filename: string;
}

// Mapear tipos de guia para cores e ícones
const tipoGuiaConfig: Record<string, { color: string; icon: React.ElementType; label: string }> = {
  PGDAS: { color: 'blue', icon: Receipt, label: 'PGDAS' },
  DAS: { color: 'green', icon: Money, label: 'DAS' },
  DARF: { color: 'purple', icon: Bank, label: 'DARF' },
  GPS: { color: 'orange', icon: FileText, label: 'GPS' },
  FGTS: { color: 'teal', icon: FileText, label: 'FGTS' },
  ISS: { color: 'pink', icon: Receipt, label: 'ISS' },
  ICMS: { color: 'cyan', icon: Receipt, label: 'ICMS' },
  default: { color: 'gray', icon: FileText, label: 'Outros' },
};

const getGuiaConfig = (tipo: string) => {
  return tipoGuiaConfig[tipo.toUpperCase()] || tipoGuiaConfig.default;
};

// Formatar competência para exibição
const formatCompetencia = (competencia: string) => {
  const [ano, mes] = competencia.split('-');
  const meses = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ];
  const mesIndex = parseInt(mes, 10) - 1;
  return `${meses[mesIndex]} ${ano}`;
};

const GuiasEmpresa = () => {
  const { empresaId } = useParams<{ empresaId: string }>();
  const navigate = useNavigate();

  const clienteDataStr = localStorage.getItem('cliente_data');
  const clienteData: ClienteData | null = clienteDataStr ? JSON.parse(clienteDataStr) : null;

  // Encontrar a empresa pelo external_id
  const empresa = useMemo(() => {
    if (!clienteData?.empresas || !empresaId) return null;
    return clienteData.empresas.find(e => e.external_id === empresaId);
  }, [clienteData?.empresas, empresaId]);

  const [periodoInicio, setPeriodoInicio] = useState(() => {
    const date = new Date();
    date.setMonth(0);
    date.setDate(1);
    return date.toISOString().split('T')[0];
  });
  const [periodoFim, setPeriodoFim] = useState(() => {
    const date = new Date();
    date.setMonth(11);
    date.setDate(31);
    return date.toISOString().split('T')[0];
  });
  const [tipoFiltro, setTipoFiltro] = useState<string>('');
  const [guias, setGuias] = useState<Guia[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [regimeEmpresa, setRegimeEmpresa] = useState<string>('');

  // Estados para o modal de visualização
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedGuia, setSelectedGuia] = useState<Guia | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Buscar guias automaticamente ao carregar
  useEffect(() => {
    if (empresaId && clienteData?.id) {
      handleBuscarGuias();
    }
  }, [empresaId]);

  const handleBuscarGuias = async (inicio?: string, fim?: string) => {
    if (!empresaId || !clienteData?.id) return;

    const dataInicio = inicio || periodoInicio;
    const dataFim = fim || periodoFim;

    setIsLoading(true);
    setHasSearched(true);

    try {
      const token = localStorage.getItem('cliente_access_token');
      const response = await api.get<GuiasResponse>(`/api/v1/secao-cliente/guias/${empresaId}`, {
        params: {
          cliente_id: clienteData.id,
          periodo_inicio: dataInicio,
          periodo_fim: dataFim,
        },
        headers: { Authorization: `Bearer ${token}` },
      });

      setGuias(response.data.data.guias);
      setRegimeEmpresa(response.data.data.regime);

      if (response.data.data.guias.length === 0) {
        toast.info('Nenhuma guia encontrada para o período selecionado');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || 'Erro ao buscar guias';
      toast.error(errorMessage);
      setGuias([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Abrir modal de visualização
  const handleOpenPreview = async (guia: Guia) => {
    if (!empresaId || !clienteData?.id) return;

    setSelectedGuia(guia);
    setIsLoadingPreview(true);
    setPreviewUrl(null);
    onOpen();

    try {
      const token = localStorage.getItem('cliente_access_token');

      // Primeiro pega a URL do S3
      const response = await api.get<DownloadResponse>(
        `/api/v1/secao-cliente/guias/${empresaId}/download`,
        {
          params: {
            cliente_id: clienteData.id,
            s3_key: guia.id,
          },
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const s3Url = response.data.url;
      setDownloadUrl(s3Url);

      // Busca o arquivo como blob para exibir no iframe
      const fileResponse = await fetch(s3Url);
      const blob = await fileResponse.blob();
      const blobUrl = URL.createObjectURL(blob);

      setPreviewUrl(blobUrl);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || 'Erro ao carregar guia';
      toast.error(errorMessage);
      onClose();
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Download direto
  const handleDownloadDirect = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
      toast.success(`Baixando ${selectedGuia?.nome_arquivo}`);
    }
  };

  // Fechar modal
  const handleCloseModal = () => {
    // Limpar blob URL para liberar memória
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedGuia(null);
    setPreviewUrl(null);
    setDownloadUrl(null);
    onClose();
  };

  // Download para baixar todas (sem abrir modal)
  const handleDownloadAll = async (guia: Guia) => {
    if (!empresaId || !clienteData?.id) return;

    setDownloadingId(guia.id);

    try {
      const token = localStorage.getItem('cliente_access_token');
      const response = await api.get<DownloadResponse>(
        `/api/v1/secao-cliente/guias/${empresaId}/download`,
        {
          params: {
            cliente_id: clienteData.id,
            s3_key: guia.id,
          },
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      window.open(response.data.url, '_blank');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || 'Erro ao baixar guia';
      toast.error(errorMessage);
    } finally {
      setDownloadingId(null);
    }
  };

  // Filtrar guias por tipo
  const guiasFiltradas = useMemo(() => {
    if (!tipoFiltro) return guias;
    return guias.filter(g => g.tipo.toUpperCase() === tipoFiltro.toUpperCase());
  }, [guias, tipoFiltro]);

  // Agrupar guias por competência
  const guiasAgrupadas = useMemo(() => {
    const grupos: Record<string, Guia[]> = {};
    guiasFiltradas.forEach(guia => {
      if (!grupos[guia.competencia]) {
        grupos[guia.competencia] = [];
      }
      grupos[guia.competencia].push(guia);
    });
    // Ordenar por competência (mais recente primeiro)
    return Object.entries(grupos).sort((a, b) => b[0].localeCompare(a[0]));
  }, [guiasFiltradas]);

  // Obter tipos únicos para o filtro
  const tiposUnicos = useMemo(() => {
    const tipos = new Set(guias.map(g => g.tipo.toUpperCase()));
    return Array.from(tipos).sort();
  }, [guias]);

  // Formatar regime para exibição
  const formatRegime = (regime: string) => {
    const regimes: Record<string, string> = {
      simples_nacional: 'Simples Nacional',
      lucro_presumido: 'Lucro Presumido',
      lucro_real: 'Lucro Real',
    };
    return regimes[regime] || regime;
  };

  // Renderizar conteúdo dos resultados
  const renderResultados = () => {
    if (isLoading) {
      return (
        <Card>
          <CardBody>
            <Flex justify="center" align="center" py={12}>
              <VStack spacing={4}>
                <Spinner size="xl" color="purple.500" thickness="4px" />
                <Text color="gray.500">Buscando guias...</Text>
              </VStack>
            </Flex>
          </CardBody>
        </Card>
      );
    }

    if (!hasSearched) {
      return (
        <Card variant="outline" bg="gray.50">
          <CardBody>
            <VStack py={12} spacing={4}>
              <Box p={4} borderRadius="full" bgGradient="linear(to-br, purple.100, purple.200)">
                <Icon as={MagnifyingGlass} boxSize={10} color="purple.400" />
              </Box>
              <VStack spacing={1}>
                <Heading size="md" color="gray.600">
                  Consulte suas guias
                </Heading>
                <Text color="gray.500" textAlign="center" maxW="400px">
                  Selecione o período desejado e clique em buscar para visualizar as guias de
                  impostos disponíveis para download.
                </Text>
              </VStack>
            </VStack>
          </CardBody>
        </Card>
      );
    }

    if (guias.length === 0) {
      return (
        <Card>
          <CardBody>
            <VStack py={12} spacing={4}>
              <Box p={4} borderRadius="full" bg="gray.100">
                <Icon as={FileText} boxSize={12} color="gray.400" />
              </Box>
              <VStack spacing={1}>
                <Heading size="md" color="gray.600">
                  Nenhuma guia encontrada
                </Heading>
                <Text color="gray.500" textAlign="center">
                  Não há guias disponíveis para o período selecionado.
                  <br />
                  Tente ajustar as datas do filtro.
                </Text>
              </VStack>
            </VStack>
          </CardBody>
        </Card>
      );
    }

    return (
      <VStack spacing={4} align="stretch">
        {/* Info da empresa e filtro de tipo */}
        <Card size="sm" bg="purple.50" borderColor="purple.100" borderWidth="1px">
          <CardBody>
            <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
              <HStack spacing={4} flexWrap="wrap">
                {regimeEmpresa && (
                  <Badge colorScheme="purple" fontSize="sm" px={3} py={1} borderRadius="full">
                    {formatRegime(regimeEmpresa)}
                  </Badge>
                )}
                <Badge colorScheme="green" fontSize="sm" px={3} py={1} borderRadius="full">
                  {guias.length} {guias.length === 1 ? 'guia encontrada' : 'guias encontradas'}
                </Badge>
              </HStack>

              {/* Filtro por tipo */}
              {tiposUnicos.length > 1 && (
                <HStack>
                  <Icon as={FunnelSimple} color="gray.500" />
                  <Select
                    size="sm"
                    placeholder="Todos os tipos"
                    value={tipoFiltro}
                    onChange={e => setTipoFiltro(e.target.value)}
                    w="180px"
                    borderRadius="lg"
                    bg="white"
                  >
                    {tiposUnicos.map(tipo => (
                      <option key={tipo} value={tipo}>
                        {getGuiaConfig(tipo).label}
                      </option>
                    ))}
                  </Select>
                  {tipoFiltro && (
                    <Tooltip label="Limpar filtro">
                      <Button size="sm" variant="ghost" onClick={() => setTipoFiltro('')} p={1}>
                        <X size={16} />
                      </Button>
                    </Tooltip>
                  )}
                </HStack>
              )}
            </Flex>
          </CardBody>
        </Card>

        {/* Lista de guias agrupadas por competência */}
        {guiasAgrupadas.map(([competencia, guiasDoMes]) => (
          <Card key={competencia} boxShadow="sm" borderRadius="xl">
            <CardBody>
              <VStack spacing={4} align="stretch">
                {/* Header do mês */}
                <Flex align="center" gap={3}>
                  <Box p={2} borderRadius="lg" bg="purple.100">
                    <Icon as={CalendarBlank} boxSize={5} color="purple.600" />
                  </Box>
                  <Heading size="sm" color="gray.700">
                    {formatCompetencia(competencia)}
                  </Heading>
                  <Badge colorScheme="gray" borderRadius="full">
                    {guiasDoMes.length} {guiasDoMes.length === 1 ? 'guia' : 'guias'}
                  </Badge>
                </Flex>

                <Divider />

                {/* Lista de guias do mês */}
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={3}>
                  {guiasDoMes.map(guia => {
                    const config = getGuiaConfig(guia.tipo);
                    const isDownloading = downloadingId === guia.id;

                    return (
                      <Card
                        key={guia.id}
                        variant="outline"
                        borderRadius="xl"
                        transition="all 0.2s"
                        _hover={{
                          borderColor: `${config.color}.300`,
                          boxShadow: 'md',
                          transform: 'translateY(-2px)',
                        }}
                        bg={cardBg}
                        borderColor={borderColor}
                      >
                        <CardBody py={4} px={4}>
                          <Flex justify="space-between" align="center" gap={3}>
                            <HStack spacing={3} flex={1} minW={0}>
                              <Box p={2} borderRadius="lg" bg={`${config.color}.50`}>
                                <Icon
                                  as={config.icon}
                                  boxSize={5}
                                  color={`${config.color}.500`}
                                  weight="duotone"
                                />
                              </Box>
                              <Box flex={1} minW={0}>
                                <HStack spacing={2} mb={1}>
                                  <Badge
                                    colorScheme={config.color}
                                    fontSize="xs"
                                    borderRadius="full"
                                  >
                                    {config.label}
                                  </Badge>
                                </HStack>
                                <Tooltip label={guia.nome_arquivo}>
                                  <Text
                                    fontSize="sm"
                                    fontWeight="500"
                                    color="gray.700"
                                    noOfLines={1}
                                  >
                                    {guia.nome_arquivo}
                                  </Text>
                                </Tooltip>
                              </Box>
                            </HStack>

                            <Tooltip label="Visualizar e baixar">
                              <Button
                                size="sm"
                                colorScheme={config.color}
                                variant="ghost"
                                onClick={() => handleOpenPreview(guia)}
                                isLoading={isDownloading}
                                borderRadius="lg"
                              >
                                <Icon as={Eye} boxSize={5} weight="bold" />
                              </Button>
                            </Tooltip>
                          </Flex>
                        </CardBody>
                      </Card>
                    );
                  })}
                </SimpleGrid>
              </VStack>
            </CardBody>
          </Card>
        ))}

        {/* Botão de baixar todos */}
        {guiasFiltradas.length > 1 && (
          <Card bg="gray.50" borderStyle="dashed" borderWidth="2px" borderColor="gray.200">
            <CardBody>
              <Flex justify="center" align="center" py={2}>
                <Button
                  variant="outline"
                  colorScheme="purple"
                  leftIcon={<FileArrowDown size={20} />}
                  onClick={() => {
                    toast.info('Iniciando download das guias...');
                    guiasFiltradas.forEach((guia, index) => {
                      setTimeout(() => handleDownloadAll(guia), index * 500);
                    });
                  }}
                  borderRadius="lg"
                >
                  Baixar todas as guias ({guiasFiltradas.length})
                </Button>
              </Flex>
            </CardBody>
          </Card>
        )}
      </VStack>
    );
  };

  if (!empresa) {
    return (
      <SecaoClienteLayout>
        <Card>
          <CardBody>
            <VStack py={12} spacing={4}>
              <Icon as={Buildings} boxSize={12} color="gray.300" />
              <Text color="gray.500">Empresa não encontrada</Text>
              <Button
                variant="outline"
                colorScheme="purple"
                leftIcon={<ArrowLeft size={18} />}
                onClick={() => navigate('/guias')}
              >
                Voltar
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </SecaoClienteLayout>
    );
  }

  return (
    <SecaoClienteLayout>
      <VStack spacing={6} align="stretch">
        {/* Breadcrumb */}
        <Breadcrumb separator={<CaretRight size={14} color="gray" />}>
          <BreadcrumbItem>
            <BreadcrumbLink
              onClick={() => navigate('/guias')}
              color="gray.500"
              _hover={{ color: 'purple.500' }}
            >
              Guias e Impostos
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <Text color="gray.700" fontWeight="500">
              {empresa.razao_social}
            </Text>
          </BreadcrumbItem>
        </Breadcrumb>

        {/* Header da empresa */}
        <Card
          bgGradient="linear(to-r, purple.500, purple.600)"
          color="white"
          borderRadius="xl"
          boxShadow="lg"
        >
          <CardBody py={5}>
            <Flex align="center" gap={4} flexWrap="wrap">
              <Box p={3} borderRadius="xl" bg="whiteAlpha.200">
                <Icon as={Buildings} boxSize={8} weight="duotone" />
              </Box>
              <Box flex={1}>
                <Heading size="md">{empresa.razao_social}</Heading>
                {empresa.cnpj && (
                  <Text fontSize="sm" opacity={0.9}>
                    CNPJ: {formatCNPJ(empresa.cnpj)}
                  </Text>
                )}
              </Box>
              <Button
                variant="outline"
                size="sm"
                color="white"
                borderColor="whiteAlpha.400"
                _hover={{ bg: 'whiteAlpha.200' }}
                leftIcon={<ArrowLeft size={16} />}
                onClick={() => navigate('/guias')}
              >
                Voltar
              </Button>
            </Flex>
          </CardBody>
        </Card>

        {/* Card de Filtros */}
        <Card boxShadow="sm" borderRadius="xl">
          <CardBody>
            <VStack spacing={5} align="stretch">
              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                <HStack>
                  <Icon as={CalendarBlank} boxSize={5} color="purple.500" />
                  <Text fontWeight="600" color="gray.700">
                    Período de busca
                  </Text>
                </HStack>

                {/* Atalhos de período */}
                <HStack spacing={2} flexWrap="wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="purple"
                    borderRadius="full"
                    isLoading={isLoading}
                    onClick={() => {
                      const now = new Date();
                      const start = new Date(now.getFullYear(), now.getMonth(), 1);
                      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                      const startStr = start.toISOString().split('T')[0];
                      const endStr = end.toISOString().split('T')[0];
                      setPeriodoInicio(startStr);
                      setPeriodoFim(endStr);
                      handleBuscarGuias(startStr, endStr);
                    }}
                  >
                    Este mês
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="purple"
                    borderRadius="full"
                    isLoading={isLoading}
                    onClick={() => {
                      const now = new Date();
                      const start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
                      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                      const startStr = start.toISOString().split('T')[0];
                      const endStr = end.toISOString().split('T')[0];
                      setPeriodoInicio(startStr);
                      setPeriodoFim(endStr);
                      handleBuscarGuias(startStr, endStr);
                    }}
                  >
                    Últimos 3 meses
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="purple"
                    borderRadius="full"
                    isLoading={isLoading}
                    onClick={() => {
                      const now = new Date();
                      const start = new Date(now.getFullYear(), now.getMonth() - 5, 1);
                      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                      const startStr = start.toISOString().split('T')[0];
                      const endStr = end.toISOString().split('T')[0];
                      setPeriodoInicio(startStr);
                      setPeriodoFim(endStr);
                      handleBuscarGuias(startStr, endStr);
                    }}
                  >
                    Últimos 6 meses
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="purple"
                    borderRadius="full"
                    isLoading={isLoading}
                    onClick={() => {
                      const now = new Date();
                      const start = new Date(now.getFullYear(), 0, 1);
                      const end = new Date(now.getFullYear(), 11, 31);
                      const startStr = start.toISOString().split('T')[0];
                      const endStr = end.toISOString().split('T')[0];
                      setPeriodoInicio(startStr);
                      setPeriodoFim(endStr);
                      handleBuscarGuias(startStr, endStr);
                    }}
                  >
                    Este ano
                  </Button>
                </HStack>
              </HStack>

              <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={4} alignItems="end">
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.600" mb={1}>
                    De
                  </FormLabel>
                  <Box
                    as="label"
                    display="flex"
                    alignItems="center"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    px={3}
                    py={2}
                    bg="white"
                    cursor="pointer"
                    transition="all 0.2s"
                    _hover={{ borderColor: 'purple.300' }}
                    _focusWithin={{
                      borderColor: 'purple.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)',
                    }}
                  >
                    <Icon as={CalendarBlank} boxSize={5} color="purple.500" mr={2} />
                    <Input
                      type="date"
                      value={periodoInicio}
                      onChange={e => setPeriodoInicio(e.target.value)}
                      border="none"
                      p={0}
                      h="auto"
                      _focus={{ boxShadow: 'none' }}
                    />
                  </Box>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" color="gray.600" mb={1}>
                    Até
                  </FormLabel>
                  <Box
                    as="label"
                    display="flex"
                    alignItems="center"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    px={3}
                    py={2}
                    bg="white"
                    cursor="pointer"
                    transition="all 0.2s"
                    _hover={{ borderColor: 'purple.300' }}
                    _focusWithin={{
                      borderColor: 'purple.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)',
                    }}
                  >
                    <Icon as={CalendarBlank} boxSize={5} color="purple.500" mr={2} />
                    <Input
                      type="date"
                      value={periodoFim}
                      onChange={e => setPeriodoFim(e.target.value)}
                      border="none"
                      p={0}
                      h="auto"
                      _focus={{ boxShadow: 'none' }}
                    />
                  </Box>
                </FormControl>

                <Box display={{ base: 'none', md: 'block' }} />

                <Button
                  colorScheme="purple"
                  leftIcon={<MagnifyingGlass size={20} weight="bold" />}
                  onClick={() => handleBuscarGuias()}
                  isLoading={isLoading}
                  loadingText="Buscando..."
                  borderRadius="lg"
                  h="42px"
                >
                  Buscar Guias
                </Button>
              </SimpleGrid>
            </VStack>
          </CardBody>
        </Card>

        {/* Resultados */}
        {renderResultados()}
      </VStack>

      {/* Modal de Visualização */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="6xl" isCentered>
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent maxH="90vh" mx={4}>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" pb={4}>
            <HStack spacing={3}>
              {selectedGuia && (
                <>
                  <Box p={2} borderRadius="lg" bg={`${getGuiaConfig(selectedGuia.tipo).color}.50`}>
                    <Icon
                      as={getGuiaConfig(selectedGuia.tipo).icon}
                      boxSize={5}
                      color={`${getGuiaConfig(selectedGuia.tipo).color}.500`}
                      weight="duotone"
                    />
                  </Box>
                  <Box>
                    <Text fontSize="lg" fontWeight="600">
                      {selectedGuia.nome_arquivo}
                    </Text>
                    <HStack spacing={2}>
                      <Badge
                        colorScheme={getGuiaConfig(selectedGuia.tipo).color}
                        fontSize="xs"
                        borderRadius="full"
                      >
                        {getGuiaConfig(selectedGuia.tipo).label}
                      </Badge>
                      <Text fontSize="sm" color="gray.500">
                        {formatCompetencia(selectedGuia.competencia)}
                      </Text>
                    </HStack>
                  </Box>
                </>
              )}
            </HStack>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody p={0} overflow="hidden">
            {isLoadingPreview && (
              <Flex justify="center" align="center" h="60vh">
                <VStack spacing={4}>
                  <Spinner size="xl" color="purple.500" thickness="4px" />
                  <Text color="gray.500">Carregando documento...</Text>
                </VStack>
              </Flex>
            )}
            {!isLoadingPreview && previewUrl && (
              <Box h="65vh" bg="gray.100">
                <iframe
                  src={previewUrl}
                  title={selectedGuia?.nome_arquivo || 'Visualização'}
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                />
              </Box>
            )}
            {!isLoadingPreview && !previewUrl && (
              <Flex justify="center" align="center" h="60vh">
                <VStack spacing={4}>
                  <Icon as={FileText} boxSize={12} color="gray.300" />
                  <Text color="gray.500">Não foi possível carregar o documento</Text>
                </VStack>
              </Flex>
            )}
          </ModalBody>

          <ModalFooter borderTop="1px solid" borderColor="gray.200" pt={4}>
            <HStack spacing={3}>
              <Button variant="ghost" onClick={handleCloseModal}>
                Fechar
              </Button>
              <Button
                colorScheme="purple"
                leftIcon={<DownloadSimple size={20} weight="bold" />}
                onClick={handleDownloadDirect}
                isDisabled={!downloadUrl}
              >
                Baixar Arquivo
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </SecaoClienteLayout>
  );
};

export default GuiasEmpresa;
